import { useTranslation } from "react-i18next";
import { ArrowLink, Button, SlotMedia, usePageAsset } from "~/components/ui";
import { buildLocalizedPath } from "~/config/route-config";
import type { Language } from "~/lib/types";

/**
 * T872 S3 — the Home's opening, from the October delivery (`components/home.page.html`
 * § hero): a dark band, the promise in serif with its accent in gold, the self-test as
 * the one primary action, «Conocer Ritual Core →» down to `#experiencia`, the estimate
 * note, and the photo on the right. Below the `lg` cut the copy stacks over the photo
 * and the primary action takes the full width.
 *
 * The photo is the `hero-landing` slot — image or video (`SlotMedia`); a video upload
 * keeps the T620 portrait twin and the T621/T653 still rules on `.ho-hero-video`. An
 * empty slot renders no figure: the copy takes the row, never a placeholder box.
 */
export function HomeHero() {
  const { t, i18n } = useTranslation("home");
  const lang = (i18n.language ?? "es") as Language;
  const photo = usePageAsset("hero-landing");

  return (
    <section className="ho-hero" id="inicio" aria-labelledby="ho-hero-title">
      <div className="pub-wrap ho-hero-content">
        <div className="ho-hero-layout">
          <div className="ho-hero-copy">
            <div className="ho-hero-heading">
              <p className="pub-eyebrow ho-hero-eyebrow">{t("hero.eyebrow")}</p>
              <h1 id="ho-hero-title">
                {t("hero.title_lead")}
                <br className="ho-desktop-break" /> <em>{t("hero.title_accent")}</em>
              </h1>
            </div>
            <p className="ho-hero-body">{t("hero.body")}</p>
            <div className="ho-actions">
              <Button variant="public-primary" href={buildLocalizedPath("autoTest", lang)}>
                {t("hero.cta")}
              </Button>
              <ArrowLink href="#experiencia" arrow="→" className="on-dark">
                {t("hero.link")}
              </ArrowLink>
            </div>
            <p className="ho-autotest-note">{t("hero.note")}</p>
          </div>
          {photo ? (
            // `sizes` is the photo column from app.css: the wrap minus gutters below 1024 px; above,
            // (wrap − 64px gap) × 1.12/2.12 with wrap = min(1280, 100vw − 160) — 224 = 160 + 64,
            // 642 = (1280 − 64) × 0.528. The T762 hero test re-derives it from the stylesheet.
            <figure className="ho-hero-photo">
              <SlotMedia
                slot="hero-landing"
                portraitSlot="hero-landing-portrait"
                variant="heroDesktop"
                loading="eager"
                fetchPriority="high"
                responsive
                sizes="(max-width: 767px) calc(100vw - 48px), (max-width: 1023px) calc(100vw - 96px), (max-width: 1439px) calc((100vw - 224px) * 0.528), 642px"
                background
                videoClassName="ho-hero-video"
                imageClassName="ho-hero-img"
              />
            </figure>
          ) : null}
        </div>
      </div>
    </section>
  );
}
